// Meta Embedded Signup helper for connecting a WhatsApp Business account.
// Loads the Facebook JS SDK on demand and launches the WhatsApp Embedded
// Signup flow, resolving with { code, waba_id, phone_number_id } which the
// backend exchanges for a business token (POST /sonic/whatsapp/connect).
//
// Requires a Meta App configured with the WhatsApp product + an Embedded Signup
// configuration; the app_id/config_id are provided by the backend
// (GET /sonic/whatsapp/connect-config).

let sdkPromise = null;

const FB_ORIGINS = ["https://www.facebook.com", "https://web.facebook.com"];

export function loadFacebookSdk(appId, graphVersion) {
  if (sdkPromise) return sdkPromise;
  sdkPromise = new Promise((resolve, reject) => {
    if (window.FB) {
      window.FB.init({
        appId,
        autoLogAppEvents: true,
        xfbml: false,
        version: graphVersion || "v21.0",
      });
      resolve(window.FB);
      return;
    }
    window.fbAsyncInit = function () {
      window.FB.init({
        appId,
        autoLogAppEvents: true,
        xfbml: false,
        version: graphVersion || "v21.0",
      });
      resolve(window.FB);
    };
    const id = "facebook-jssdk";
    if (document.getElementById(id)) return;
    const script = document.createElement("script");
    script.id = id;
    script.async = true;
    script.defer = true;
    script.crossOrigin = "anonymous";
    script.src = "https://connect.facebook.net/en_US/sdk.js";
    script.onerror = () => {
      sdkPromise = null;
      reject(new Error("sdk_load_failed"));
    };
    document.body.appendChild(script);
  });
  return sdkPromise;
}

/**
 * Launch the WhatsApp Embedded Signup popup.
 * Resolves { code, waba_id, phone_number_id }, or rejects on cancel/error.
 */
export function launchEmbeddedSignup({ appId, configId, graphVersion }) {
  return new Promise((resolve, reject) => {
    loadFacebookSdk(appId, graphVersion)
      .then((FB) => {
        let sessionInfo = {};

        const onMessage = (event) => {
          if (!FB_ORIGINS.includes(event.origin)) return;
          try {
            const data =
              typeof event.data === "string" ? JSON.parse(event.data) : event.data;
            if (data && data.type === "WA_EMBEDDED_SIGNUP") {
              if (data.event === "FINISH" && data.data) {
                sessionInfo = {
                  waba_id: data.data.waba_id,
                  phone_number_id: data.data.phone_number_id,
                };
              } else if (data.event === "CANCEL") {
                finish(new Error("cancelled"));
              }
            }
          } catch (_) {
            /* ignore non-JSON messages */
          }
        };

        let settled = false;
        const cleanup = () => window.removeEventListener("message", onMessage);
        const finish = (err, value) => {
          if (settled) return;
          settled = true;
          cleanup();
          if (err) reject(err);
          else resolve(value);
        };
        window.addEventListener("message", onMessage);

        const extras = { setup: {}, sessionInfoVersion: "3" };
        const options = {
          response_type: "code",
          override_default_response_type: true,
          extras,
        };
        if (configId) {
          options.config_id = configId;
        } else {
          extras.feature = "whatsapp_embedded_signup";
          extras.version = 2;
        }
        FB.login(
          (response) => {
            const code =
              response && response.authResponse && response.authResponse.code;
            const deliver = () => {
              if (code) finish(null, { code, ...sessionInfo });
              else finish(new Error("no_code"));
            };
            if (sessionInfo.phone_number_id) deliver();
            else setTimeout(deliver, 500);
          },
          options
        );
      })
      .catch(reject);
  });
}
